import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderProps,
  BorderRadiusProps,
  PositionProps,
  SpaceProps,
} from 'styled-system';
import { backgroundColor, border, borderRadius, position, space, themeGet } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import { breakpoint } from 'common/theme/helper';
import { focusOutlineCSSWithOffset } from 'common/theme/styles';
import type { CursorProps, ThemeHeightProps, ThemeWidthProps } from 'common/theme/system-utilities';
import { cursor, themeHeight, themeWidth } from 'common/theme/system-utilities';

export interface DotProps {
  x: number;
  y: number;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  isActive: boolean;
  ariaDescribedBy?: string;
  dotRef?: React.RefObject<HTMLButtonElement>;
}

const StyledDot = styled.button<
  PositionProps &
    ThemeWidthProps &
    ThemeHeightProps &
    BorderProps &
    BorderRadiusProps &
    CursorProps &
    SpaceProps &
    BackgroundColorProps & { isActive: boolean }
>`
  ${position};
  ${themeWidth};
  ${themeHeight};
  ${border};
  ${borderRadius};
  ${cursor};
  ${space};
  ${backgroundColor};

  &::before {
    content: '';
    position: absolute;
    border-radius: 50%;
    ${({ isActive }) => `
        width: ${isActive ? '6px' : '10px'};
        height: ${isActive ? '6px' : '10px'};
        top: ${isActive ? '8px' : '6px'};
        left: ${isActive ? '8px' : '6px'};
    `}
    background-color: ${({ isActive }) =>
      isActive
        ? themeGet('colors.sys.primary.background.default')
        : themeGet('colors.sys.neutral.background.default')};
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    z-index: 0;
    background-color: #000000;
    opacity: ${({ isActive }) => (isActive ? '1' : '0.6')};
    border: 1px solid ${themeGet('colors.sys.neutral.border.inverted')};
  }

  ${breakpoint(1)} {
    &::before {
      ${({ isActive }) => `
        width: ${isActive ? '12px' : '18px'};
        height: ${isActive ? '12px' : '18px'};
        top: ${isActive ? '15px' : '12px'};
        left: ${isActive ? '15px' : '12px'};
    `}
    }

    &::after {
      width: 30px;
      height: 30px;
      top: 5px;
      left: 5px;
      border: 1px solid ${themeGet('colors.sys.neutral.border.inverted')};
    }
  }

  ${focusOutlineCSSWithOffset};
`;

const dotStyles: Partial<ComponentProps<typeof StyledDot>> = {
  position: 'absolute',
  themeWidth: ['sp_20', 'sp_40', null, 'sp_40'],
  themeHeight: ['sp_20', 'sp_40', null, 'sp_40'],
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  padding: 0,
  backgroundColor: 'transparent',
};

const getClampedCoordinate = (value: number) => {
  return Math.max(0.06, Math.min(0.94, value));
};

export const Dot = ({ onClick, x, y, isActive, dotRef, ...props }: DotProps) => {
  const { t } = useLocalization();

  return (
    <StyledDot
      onClick={e => {
        e.preventDefault();
        onClick();
      }}
      type="button"
      aria-label={t('h24_shopthelook_interactive_dot')}
      style={{
        top: `${getClampedCoordinate(y) * 100}%`,
        left: `${getClampedCoordinate(x) * 100}%`,
      }}
      isActive={isActive}
      ref={dotRef}
      {...dotStyles}
      {...props}
    />
  );
};
